'use client'

import { cn } from '@/lib/utils'
import { BaseLink, Icon } from '@/ui/components'
import { useLenis } from 'lenis/react'
import { usePathname } from 'next/navigation'
import React, { memo, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { ButtonProps } from './type'

const Button = ({
  children,
  size = 'l',
  variant = 'primary',
  mode = 'default',
  className,
  onMouseEnter,
  onMouseLeave,
  isCurrent,
  nextLink,
  animHover,
  icon,
  iconPosition,
  leftIcon,
  rightIcon,
  label,
  allowDisable = false,
  iconClassName,
  ...props
}: ButtonProps) => {
  // normalize size
  size = size == 'xs' ? 's' : size

  //states
  const [isDisabled, setIsDisabled] = useState<boolean>(false)
  const lenis = useLenis()
  const pathName = usePathname()

  useEffect(() => {
    if (allowDisable) {
      if (pathName === props.href) {
        setIsDisabled(true)
      } else {
        setIsDisabled(false)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName])

  const variantAndMode = {
    primary: {
      modes: {
        default: 'bg-primary-400 text-neutral-100 hover:bg-primary-300',
        invert: 'bg-neutral-100 text-neutral-600 hover:bg-white'
      },
      sizes: {
        s: 'px-md py-xs',
        m: 'px-md py-xs',
        l: 'px-lg py-sm'
      }
    },
    secondary: {
      modes: {
        default: 'bg-neutral-200 text-neutral-600 hover:bg-neutral-300',
        invert: 'border border-white bg-white/0 text-white hover:bg-white/10'
      },
      sizes: {
        s: 'px-md py-xs',
        m: 'px-md py-xs',
        l: 'px-lg py-sm'
      }
    },
    ghost: {
      modes: {
        default: 'text-neutral-600 hover:bg-black/10',
        invert: 'text-white hover:bg-white/10'
      },
      sizes: {
        s: 'px-md py-xs',
        m: 'px-md py-xs',
        l: 'px-lg py-sm'
      }
    },
    tertiary: {
      modes: {
        default: 'text-neutral-600',
        invert: 'text-white hover:text-white/60 hover:underline'
      },
      sizes: {
        s: '',
        m: '',
        l: ''
      }
    },
    text: {
      modes: {
        default: 'text-neutral-600 items-start [&_svg]:mt-[0.24em]',
        invert:
          'text-white items-start [&_svg]:mt-[0.24em] hover:text-white/60 hover:underline'
      },
      sizes: {
        s: '',
        m: '',
        l: ''
      }
    },
    shop: {
      modes: {
        default: 'bg-shop-300 text-white gap-xs',
        invert: 'bg-shop-300 text-white gap-xs'
      },
      sizes: {
        s: 'px-md py-xs',
        m: 'px-md py-xs',
        l: 'px-lg py-sm'
      }
    }
  }

  const fontSizeClass = twMerge(
    size === 's' && 'textstyle-sm-button',
    size === 'm' && 'textstyle-md-button',
    size === 'l' && 'textstyle-md-button'
  )

  const classCompser = cn(
    'c-button group/button relative inline-flex items-center justify-center gap-2xs transition-all duration-200 cursor-pointer focus:z-10 disabled:pointer-events-none disabled:opacity-50',
    variantAndMode[variant]?.sizes[size],
    variantAndMode[variant]?.modes[mode],
    fontSizeClass,
    isDisabled && 'pointer-events-none opacity-50',
    className
  )

  const iconClassNameComposer = twMerge(
    'items-center justify-center relative z-2',
    size === 'm' && 'w-4 h-4',
    size === 'l' && 'w-5 h-5',
    size === 's' && 'w-3.5',
    iconClassName
  )

  const mouseEnter = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (onMouseEnter) {
      onMouseEnter(e)
    }
  }

  const mouseLeave = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (onMouseLeave) {
      onMouseLeave(e)
    }
  }

  const onClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (props.onClick) {
      props.onClick(e)
    } else {
      if (props.href?.startsWith('#')) {
        e.preventDefault()

        if (!lenis) {
          return
        }
        lenis?.scrollTo(props.href, { offset: -10 })
      }
    }
  }

  if (icon || iconPosition) {
    leftIcon = iconPosition === 'left' ? icon : undefined
    rightIcon = iconPosition === 'right' ? icon : undefined
  }

  const ButtonContent = (
    <>
      {leftIcon && (
        <div className={iconClassNameComposer} data-icon>
          {typeof leftIcon === 'string' ? (
            <Icon className="h-full w-full" icon={leftIcon} />
          ) : (
            leftIcon
          )}
        </div>
      )}
      <span
        data-text
        className="z-2 flex h-full items-center transition-transform group-hover/button:translate-x-0.5"
      >
        {children || label}
      </span>
      {rightIcon && (
        <div className={iconClassNameComposer} data-icon>
          {typeof rightIcon === 'string' ? (
            <Icon className="h-full w-full" icon={rightIcon} />
          ) : (
            rightIcon
          )}
        </div>
      )}
    </>
  )

  return (
    <BaseLink
      className={classCompser}
      nextLink={!props.href ? false : nextLink}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
      onClick={onClick}
      isCurrent={isCurrent}
      data-anim-hover={cn(
        animHover || animHover === false ? animHover : variant
      )}
      {...props}
    >
      {ButtonContent}
    </BaseLink>
  )
}

Button.displayName = 'Button'

export default memo(Button)
